import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { H5PEditorUI } from "@lumieducation/h5p-react";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Save } from "lucide-react";
import { Check } from "lucide-react";
import { Loader2 } from "lucide-react";
import { Button } from "@core/button";
import { ArrowLeft } from "lucide-react";
import { getEnvironment } from "@packages/utils";

const AdvancedEditView = ({ id }: { id: string }) => {
  const { t, i18n } = useTranslation();
  const editorRef = useRef<any>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [saved, setSaved] = useState<boolean>(false);
  const navigate = useNavigate();
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  const handleFinish = () => {
    navigate(`/content/interactive-materials/${id}`);
  };

  useEffect(() => {
    const applyIframeStyles = () => {
      if (!editorRef.current?.h5pEditor.current) return;

      const editorElement = editorRef.current.h5pEditor.current;
      const iframe = editorElement.querySelector(
        ".h5p-editor-iframe",
      ) as HTMLIFrameElement;

      if (iframe && iframe.contentDocument) {
        const hubElement = iframe.contentDocument.querySelector(".h5p-hub");
        if (hubElement) {
          (hubElement as HTMLElement).style.display = "none";
        }
        const tutorialUrlElement =
          iframe.contentDocument.querySelector(".h5p-tutorial-url");
        if (tutorialUrlElement) {
          (tutorialUrlElement as HTMLElement).style.display = "none";
        }
        const exampleUrlElement =
          iframe.contentDocument.querySelector(".h5p-example-url");
        if (exampleUrlElement) {
          (exampleUrlElement as HTMLElement).style.display = "none";
        }
        const copypasteWrapElement = iframe.contentDocument.querySelector(
          ".h5peditor-copypaste-wrap",
        );
        if (copypasteWrapElement) {
          (copypasteWrapElement as HTMLElement).style.display = "none";
        }
        const htmlElement = iframe.contentDocument.querySelector("html");
        if (htmlElement) {
          (htmlElement as HTMLElement).style.margin = "0 auto";
        }
      }
    };

    // Set up an interval to check and apply styles
    const intervalId = setInterval(applyIframeStyles, 1000);

    // Clean up interval on unmount
    return () => clearInterval(intervalId);
  }, []);

  // Save content callback
  const saveContentCallback = async (id: string, contentData: any) => {
    try {
      const res = await fetch(
        `https://h5p.madlen.io/h5p/${id}/${i18n.language}${getEnvironment() === "DEV" ? "?dev=true" : ""}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(contentData),
        },
      );

      const responseData = await res.json();
      return responseData;
    } catch (error) {
      console.error("Error saving content:", error);
      throw error;
    }
  };

  const renderButtons = () => {
    return (
      <div className="flex items-center justify-between my-6 gap-4">
        <Button
          onClick={() => handleFinish()}
          variant="ghost"
          size="lg"
          rounded="xl"
          className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700/50 dark:hover:bg-gray-700/70 border-none"
          textColor="text-gray-700 dark:text-gray-200"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">{t("common.back")}</span>
        </Button>

        <Button
          onClick={() => {
            setSaving(true);
            editorRef.current?.save();
          }}
          disabled={saving || !isLoaded}
          variant="solid"
          size="lg"
          rounded="xl"
          className="flex items-center gap-2"
          bgColor={saved ? "bg-green-500" : "bg-rose-500 hover:bg-rose-600"}
          textColor="text-white"
        >
          {saving ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : saved ? (
            <Check className="w-5 h-5" />
          ) : (
            <Save className="w-5 h-5" />
          )}
          <span className="font-medium">{t("common.save")}</span>
        </Button>
      </div>
    );
  };

  const renderLoadingScreen = () => {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl z-10">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 text-rose-500 animate-spin" />
          <p className="text-lg font-medium text-gray-700 dark:text-gray-200">
            {t("common.loading")}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-rose-50 dark:from-gray-900 dark:to-rose-900">
      <div className="container max-w-7xl mx-auto px-4 py-4 sm:py-8">
        <div className="space-y-6 sm:space-y-8">
          <div className="relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-xl border border-gray-200/50 dark:border-gray-700/50">
            <div className="px-4 sm:px-6 min-h-96">
              {!isLoaded && renderLoadingScreen()}
              <div className={!isLoaded ? "opacity-0" : "opacity-100"}>
                {renderButtons()}
                <H5PEditorUI
                  ref={editorRef}
                  contentId={id}
                  loadContentCallback={async (id: string) => {
                    try {
                      console.log(`Loading content with ID: ${id}`);
                      return (
                        await fetch(
                          `https://h5p.madlen.io/h5p/${id}/edit/${i18n.language}${getEnvironment() === "DEV" ? "?dev=true" : ""}`,
                        )
                      ).json();
                    } catch (error) {
                      console.error("Error loading content:", error);
                      return {};
                    }
                  }}
                  onLoaded={() => {
                    console.log("Content loaded");
                    setIsLoaded(true);
                  }}
                  saveContentCallback={saveContentCallback}
                  onSaved={() => {
                    setSaving(false);
                    setSaved(true);

                    setTimeout(() => {
                      setSaved(false);
                      handleFinish();
                    }, 300);
                  }}
                  onSaveError={(event) => {
                    setSaving(false);
                    setSaved(false);
                    console.error("Save error", event);
                  }}
                />
                {renderButtons()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedEditView;
